import React, { useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './App.css';

function App() {
  const [activeNav, setActiveNav] = useState('home');

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Bear Hug Bounce for Illustrations
    gsap.fromTo('.bear-illustration', { y: 10 }, { y: -10, duration: 1.5, repeat: -1, yoyo: true, ease: 'sine.inOut' });

    // Glowing Button Hovers
    document.querySelectorAll('.cta-button').forEach(button => {
      button.addEventListener('mouseenter', () => {
        gsap.to(button, { boxShadow: '0 0 15px #FFAB40', scale: 1.05, duration: 0.3, repeat: 1, yoyo: true });
      });
      button.addEventListener('mouseleave', () => {
        gsap.to(button, { boxShadow: '0 0 0 #FFAB40', scale: 1, duration: 0.3 });
      });
    });

    // Paw Print Trails on Scroll
    gsap.utils.toArray('.section').forEach(section => {
      gsap.fromTo(
        section.querySelectorAll('.paw-print'),
        { opacity: 0, y: 20 },
        { opacity: 0.2, y: 0, stagger: 0.3, duration: 0.5, ease: 'power2.out', scrollTrigger: { trigger: section, start: 'top 80%' } }
      );
    });

    // Background Glow Pulse
    gsap.to('.App', {
      background: 'radial-gradient(circle at center, #FFC107 0%, #FFF8E1 70%, #FFAB40 100%)',
      duration: 5,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut'
    });

    // Cleanup event listeners on unmount
    return () => {
      document.querySelectorAll('.cta-button').forEach(button => {
        button.removeEventListener('mouseenter', () => {});
        button.removeEventListener('mouseleave', () => {});
      });
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveNav(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <img src="/friends.png" alt="Brown Bear Hug" className="bear-illustration" />
          <h1 className="header-tagline">Brown’s Bear Hugs  Always Here for You!</h1>
          <p className="header-subtext">Join $BROWN on Base chain for cozy vibes and bear hugs through every market dip and pump!</p>
          <button className="cta-button" onClick={() => scrollToSection('features')}>
            Hug In!
          </button>
        </div>
      </header>

      <nav className="nav">
        <a href="#about" className={activeNav === 'about' ? 'active' : ''} onClick={() => scrollToSection('about')}>
          About
        </a>
        <a href="#features" className={activeNav === 'features' ? 'active' : ''} onClick={() => scrollToSection('features')}>
          Features
        </a>
        <a href="#tokenomics" className={activeNav === 'tokenomics' ? 'active' : ''} onClick={() => scrollToSection('tokenomics')}>
          Tokenomics
        </a>
        <a href="#roadmap" className={activeNav === 'roadmap' ? 'active' : ''} onClick={() => scrollToSection('roadmap')}>
          Roadmap
        </a>
      </nav>

      <section className="section about-section" id="about">
        <h2 className="section-heading">Meet Brown – Your Bear Buddy</h2>
        <p className="section-content">
          Brown is the grumpiest bear with the biggest heart on Base chain! Inspired by LINE Friends, $BROWN is here to be your shoulder to lean on, offering bear hugs to degens through every bull and bear market. With his loyal vibe and cozy hugs, Brown brings the Base chain community together, one cuddle at a time.
        </p>
        <img src="/brown2.png" alt="Brown and Hug Bears" className="about-visual" />
        <div className="paw-print" style={{ left: '5%', top: '10%' }}>🐾</div>
        <div className="paw-print" style={{ right: '5%', bottom: '10%' }}>🐾</div>
      </section>

      <section className="section features-section" id="features">
        <h2 className="section-heading">Cozy Features for Degens</h2>
        <div className="features-grid">
          <div className="feature-card">
            <h3 className="feature-subheading">Bear Hug Hub</h3>
            <p className="feature-content">
              Join our Bear Hug Hub on X or Discord to share wholesome memes, bear hug GIFs, and supportive messages. Brown’s always here to cheer you up!
            </p>
            <img src="/removebg.png" alt="Brown with Heart Balloon" className="bear-illustration feature-visual" />
          </div>
          <div className="feature-card">
            <h3 className="feature-subheading">Hug Spotlight (Post-Vesting)</h3>
            <p className="feature-content">
              After our 45-day vesting period, vote on the best bear hug meme or message each week, and get spotlighted on our X page! Let’s spread positivity together.
            </p>
            <img src="/removebg.png" alt="Brown Thumbs Up" className="feature-visual" />
          </div>
        </div>
        <div className="paw-print" style={{ left: '3%', top: '20%' }}>🐾</div>
        <div className="paw-print" style={{ right: '3%', bottom: '20%' }}>🐾</div>
      </section>

      <section className="section tokenomics-section" id="tokenomics">
        <h2 className="section-heading">$BROWN Tokenomics</h2>
        <p className="section-content">
          Total Supply: 1,000,000,000 $BROWN<br />
          Allocation: 95% Locked in LP, 5% Team (vested for 45 days)<br />
          Utility: Join the Bear Hug Hub now, and vote in the Hug Spotlight post-vesting!
        </p>
        <div className="tokenomics-visual">
          <img src="/chart2.png" alt="Bear Paw Pie Chart" className="section-visual" />
        </div>
        <div className="paw-print" style={{ left: '5%', top: '15%' }}>🐾</div>
        <div className="paw-print" style={{ right: '5%', bottom: '15%' }}>🐾</div>
      </section>

      <section className="section how-to-buy-section" id="how-to-buy">
        <h2 className="section-heading">How to Buy $BROWN</h2>
        <p className="section-content">
          Brown’s here to guide you through getting your $BROWN tokens on Base chain, let’s make it cozy and simple!
        </p>
        <div className="how-to-buy-steps">
          <div className="how-to-buy-step">
            <span className="step-number">1</span>
            <p className="step-content">
              Set up a wallet like MetaMask or Coinbase Wallet and connect it to the Base chain. Brown’s got you covered!
            </p>
          </div>
          <div className="how-to-buy-step">
            <span className="step-number">2</span>
            <p className="step-content">
              Grab some ETH on an exchange like Coinbase or Binance, then transfer it to your wallet. You’re halfway there!
            </p>
          </div>
          <div className="how-to-buy-step">
            <span className="step-number">3</span>
            <p className="step-content">
              Head to <a href="https://app.uniswap.org" target="_blank" rel="noopener noreferrer">Uniswap</a>, connect your wallet, and swap ETH for $BROWN using the official contract address.
            </p>
          </div>
          <div className="how-to-buy-step">
            <span className="step-number">4</span>
            <p className="step-content">
              Confirm the transaction, then check your wallet for $BROWN tokens. Stay safe, always verify the contract address on our <a href="https://x.com/BrownBearHugs" target="_blank" rel="noopener noreferrer">X page</a>!
            </p>
          </div>
        </div>
        <img src="/ath.png" alt="Brown Guiding You" className="bear-illustration section-visual" />
        <div className="paw-print" style={{ left: '5%', top: '10%' }}>🐾</div>
        <div className="paw-print" style={{ right: '5%', bottom: '10%' }}>🐾</div>
      </section>

      <section className="section roadmap-section" id="roadmap">
        <h2 className="section-heading">Brown’s Cozy Journey</h2>
        <div className="roadmap-timeline">
          <div className="roadmap-item">
            <h3 className="roadmap-date">Q2 2025</h3>
            <p className="roadmap-content">Launch on Base chain via Cliza Systems, with the Bear Hug Hub rollout.</p>
          </div>
          <div className="roadmap-item">
            <h3 className="roadmap-date">Q3 2025 (Post-Vesting)</h3>
            <p className="roadmap-content">Introduce the Hug Spotlight feature to spread positivity.</p>
          </div>
          <div className="roadmap-item">
            <h3 className="roadmap-date">Q4 2025</h3>
            <p className="roadmap-content">Amplify X hype with wholesome meme contests.</p>
          </div>
          <div className="roadmap-item">
            <h3 className="roadmap-date">2026</h3>
            <p className="roadmap-content">Plan a “Bear Hug Festival” with other Base chain tokens, if we grow!</p>
          </div>
        </div>
        <div className="paw-print" style={{ left: '3%', top: '25%' }}>🐾</div>
        <div className="paw-print" style={{ right: '3%', bottom: '25%' }}>🐾</div>
      </section>

      <footer className="footer" id="join">
        <div className="footer-links">
          <a href="#about">About</a>
          <a href="#features">Features</a>
          <a href="#tokenomics">Tokenomics</a>
          <a href="#roadmap">Roadmap</a>
        </div>
        <div className="footer-socials">
          <a href="https://x.com/BrownBearHugs" target="_blank" rel="noopener noreferrer" className="social-icon">🐻</a>
          <a href="https://discord.gg/BrownBearHugs" target="_blank" rel="noopener noreferrer" className="social-icon">🐻</a>
        </div>
        <p className="footer-tagline">$BROWN – A Hug for Every Degen on Base Chain</p>
      </footer>
    </div>
  );
}

export default App;